import axios, { AxiosError } from "axios";

const BASE_URL = process.env.REACT_APP_API_URL || "/api/v1";

export const api = axios.create({
  baseURL: BASE_URL,
  headers: { "Content-Type": "application/json" },
  timeout: 120000,
});

// Request interceptor - attach token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor - handle 401
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem("refresh_token");
      if (refreshToken) {
        try {
          const res = await axios.post(`${BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });
          const { access_token } = res.data;
          localStorage.setItem("access_token", access_token);
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        } catch {
          localStorage.clear();
          window.location.href = "/login";
        }
      } else {
        localStorage.clear();
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

export const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.detail || error.message || "An error occurred";
  }
  return "An unexpected error occurred";
};

// Auth
export const authAPI = {
  register: (data: any) => api.post("/auth/register", data),
  login: (data: any) => api.post("/auth/login", data),
  me: () => api.get("/auth/me"),
  updateProfile: (data: any) => api.put("/auth/me", data),
  changePassword: (data: any) => api.post("/auth/change-password", data),
};

// Products
export const productsAPI = {
  list: (params?: any) => api.get("/products", { params }),
  get: (id: string) => api.get(`/products/${id}`),
  getBySlug: (slug: string) => api.get(`/products/slug/${slug}`),
  create: (data: any) => api.post("/products", data),
  update: (id: string, data: any) => api.put(`/products/${id}`, data),
  delete: (id: string) => api.delete(`/products/${id}`),
  uploadImage: (id: string, formData: FormData) =>
    api.post(`/products/${id}/images`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  deleteImage: (productId: string, imageId: string) =>
    api.delete(`/products/${productId}/images/${imageId}`),
};

// Cart
export const cartAPI = {
  get: () => api.get("/cart"),
  addItem: (data: any) => api.post("/cart/items", data),
  updateItem: (itemId: string, data: any) => api.put(`/cart/items/${itemId}`, data),
  removeItem: (itemId: string) => api.delete(`/cart/items/${itemId}`),
  clear: () => api.delete("/cart"),
};

// Orders
export const ordersAPI = {
  create: (data: any) => api.post("/orders", data),
  list: (params?: any) => api.get("/orders", { params }),
  get: (id: string) => api.get(`/orders/${id}`),
  adminList: (params?: any) => api.get("/orders/admin/all", { params }),
  adminUpdateStatus: (id: string, data: any) =>
    api.put(`/orders/admin/${id}/status`, data),
  adminRefund: (id: string) => api.post(`/orders/admin/${id}/refund`),
};

// Payments
export const paymentsAPI = {
  createOrder: (data: any) => api.post("/payments/create-order", data),
  verify: (data: any) => api.post("/payments/verify", data),
};

// Returns
export const returnsAPI = {
  create: (data: any) => api.post("/returns", data),
  myReturns: () => api.get("/returns/my"),
  adminList: () => api.get("/returns/admin/all"),
  adminUpdate: (id: string, data: any) => api.put(`/returns/admin/${id}`, data),
};

// Support
export const supportAPI = {
  createTicket: (data: any) => api.post("/support/tickets", data),
  myTickets: () => api.get("/support/tickets/my"),
  getTicket: (id: string) => api.get(`/support/tickets/${id}`),
  addMessage: (ticketId: string, data: any) =>
    api.post(`/support/tickets/${ticketId}/messages`, data),
  adminTickets: (params?: any) => api.get("/support/admin/tickets", { params }),
  adminUpdateTicket: (id: string, data: any) =>
    api.put(`/support/admin/tickets/${id}`, data),
  sendChatMessage: (data: any) => api.post("/support/chat/message", data),
  getChatHistory: (sessionId: string) =>
    api.get(`/support/chat/history/${sessionId}`),
  contactForm: (data: any) => api.post("/support/contact", data),
};

// Admin
export const adminAPI = {
  dashboard: () => api.get("/admin/dashboard"),
  revenueAnalytics: (days?: number) =>
    api.get("/admin/analytics/revenue", { params: { days } }),
  topProducts: (limit?: number) =>
    api.get("/admin/analytics/top-products", { params: { limit } }),
  users: (params?: any) => api.get("/admin/users", { params }),
};

// Wishlist
export const wishlistAPI = {
  get: () => api.get("/wishlist"),
  add: (productId: string) => api.post(`/wishlist/${productId}`),
  remove: (productId: string) => api.delete(`/wishlist/${productId}`),
};
